/**
 * Chat Context
 *
 * Unified context for all chat functionality
 * Composes modular services, subscriptions, and state management
 */

"use client";

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { usePathname } from "next/navigation";
import { useAuth } from "@/context/providers/AuthProvider";
import { EnhancedMessage } from "@/types/centralizedChat";
import { ChatState, initialChatState } from "./chat/chatState";
import { ChatContextValue, ChatActions, ChatMetadata } from "./chat/types";
import { createChatActions } from "./chat/chatActions";
import { SubscriptionManager } from "./chat/subscriptions/SubscriptionManager";
import { MessageSubscriptionConfig } from "./chat/subscriptions/messageSubscription";
import { ConversationSubscriptionConfig } from "./chat/subscriptions/conversationSubscription";
import { messageCache } from "./chat/cache/messageCache";

const ChatContext = createContext<ChatContextValue | undefined>(undefined);

interface ChatProviderProps {
  children: React.ReactNode;
}

export function ChatProvider({ children }: ChatProviderProps) {
  const { user, roles, loading: authLoading } = useAuth();
  const pathname = usePathname();
  const [state, setState] = useState<ChatState>(initialChatState);
  const subscriptionManagerRef = useRef<SubscriptionManager | null>(null);
  const stateRef = useRef<ChatState>(state);
  const selectedConversationRef =
    useRef<ChatState["selectedConversation"]>(null);

  // Keep stateRef in sync with state
  useEffect(() => {
    stateRef.current = state;
    selectedConversationRef.current = state.selectedConversation;
  }, [state]);

  // Check if we're on the messages page
  const isMessagesPage = pathname?.startsWith("/messages") ?? false;

  // Initialize subscription manager
  if (!subscriptionManagerRef.current) {
    subscriptionManagerRef.current = new SubscriptionManager();
  }

  const subscriptionManager = subscriptionManagerRef.current;

  // Get state getter for actions - use ref to prevent recreation
  const getState = useCallback(() => stateRef.current, []);

  // Create actions - only recreate when user or roles change
  const actions = useMemo<ChatActions>(() => {
    return createChatActions({
      userId: user?.id || null,
      user: user || null,
      roles,
      setState,
      getState,
    });
  }, [user?.id, roles, getState]);

  // Metadata
  const metadata = useMemo<ChatMetadata>(() => {
    return {
      userRole: roles?.admin
        ? "admin"
        : roles?.manufacturer
          ? "manufacturer"
          : "customer",
      userId: user?.id || null,
    };
  }, [user, roles]);

  // Handle new message from subscription - use ref to avoid dependency on state
  const handleNewMessage = useCallback((message: EnhancedMessage) => {
    setState(prevState => {
      // Check if message already exists
      if (prevState.messages.some(msg => msg.id === message.id)) {
        return prevState;
      }

      // Add message to end
      return {
        ...prevState,
        messages: [...prevState.messages, message],
      };
    });

    // Invalidate cache using ref to avoid dependency
    const currentConversation = selectedConversationRef.current;
    if (currentConversation) {
      messageCache.invalidate(currentConversation.conversationId);
    }
  }, []);

  // Set up message subscription when conversation is selected
  useEffect(() => {
    if (
      authLoading ||
      !user ||
      !state.selectedConversation ||
      !isMessagesPage
    ) {
      if (!isMessagesPage) {
        subscriptionManager.cleanupMessageSubscription();
      }
      return;
    }

    const config: MessageSubscriptionConfig = {
      conversationId: state.selectedConversation.conversationId,
      context: {
        user: user || { id: "", full_name: null },
        roles,
        conversation: state.selectedConversation,
      },
      callbacks: {
        onNewMessage: handleNewMessage,
        onMarkAsRead: actions.markAsRead,
      },
      enabled: true,
    };

    subscriptionManager.setupMessageSubscription(config);

    return () => {
      subscriptionManager.cleanupMessageSubscription();
    };
  }, [
    user?.id,
    authLoading,
    state.selectedConversation?.conversationId,
    isMessagesPage,
    roles,
    handleNewMessage,
    actions.markAsRead,
  ]);

  // Set up conversation subscription - only on messages page
  useEffect(() => {
    if (authLoading || !user || !isMessagesPage) {
      if (!isMessagesPage) {
        subscriptionManager.cleanupConversationSubscription();
      }
      return;
    }

    const config: ConversationSubscriptionConfig = {
      userId: user.id,
      callbacks: {
        onConversationUpdate: () => {
          actions.refetchConversations();
        },
      },
      enabled: true,
    };

    subscriptionManager.setupConversationSubscription(config);

    return () => {
      subscriptionManager.cleanupConversationSubscription();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, authLoading, isMessagesPage]);

  // Set up connection monitor - only on messages page
  useEffect(() => {
    if (!isMessagesPage) {
      subscriptionManager.cleanup();
      return;
    }

    subscriptionManager.setupConnectionMonitor({
      onStatusChange: status => {
        setState(prevState => ({
          ...prevState,
          connectionStatus: status,
          isConnected: status === "connected",
        }));
      },
    });

    return () => {
      subscriptionManager.cleanup();
    };
  }, [isMessagesPage]);

  // Initialize conversations when user is ready - only on messages page
  useEffect(() => {
    if (authLoading || !user || !isMessagesPage) return;
    actions.fetchConversations();
    actions.refreshNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, authLoading, isMessagesPage]);

  // Server-side auth keeps Supabase tokens out of the browser, so chat uses
  // authenticated API polling instead of browser Supabase realtime.
  useEffect(() => {
    if (authLoading || !user || !isMessagesPage) return;

    const intervalId = window.setInterval(() => {
      actions.refreshNotifications();
      actions.refetchConversations();

      const selectedConversation = selectedConversationRef.current;
      if (selectedConversation) {
        actions.loadMessages(selectedConversation.conversationId, 1);
      }
    }, 15_000);

    return () => window.clearInterval(intervalId);
  }, [actions, authLoading, isMessagesPage, user?.id]);

  // Update connection status from subscription manager
  useEffect(() => {
    const status = subscriptionManager.getConnectionStatus();
    setState(prevState => ({
      ...prevState,
      connectionStatus: status,
      isConnected: status === "connected",
    }));
  }, []);

  // Cleanup all subscriptions when component unmounts
  useEffect(() => {
    return () => {
      subscriptionManager.cleanup();
    };
  }, [subscriptionManager]);

  const value: ChatContextValue = useMemo(
    () => ({
      state,
      actions,
      metadata,
    }),
    [state, actions, metadata]
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat(): ChatContextValue {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
}
